import {
  IFailedCustomMappingRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Firestore,
  XSLXImporterExporter,
  resolveWithConcurrencyForAll,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { DELETE_CONCURRENCY } from '../../../base-custom-mapping-handler';
import { IBaseMigrationItemCode } from '../../../interfaces';
import { BaseItemCodeToTreatmentMappingHandler } from '../../../mappings/base-item-code-to-treatment';
import { XLSXToItemCodeToTreatment } from '../../../mappings/xlsx-to-item-code-to-treatment';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticeItemCode,
  ICorePracticeItemCode,
  ItemCodeSourceEntity,
} from '../../source/entities/item-codes';

function normalise(name: string): string {
  return name.trim().toLowerCase().replace(/,/g, '');
}

interface ISourceItemInfo {
  sourceIdentifier: string;
  sourceLabel: string;
}

export class CorePracticeItemCodeToTreatmentMappingHandler extends BaseItemCodeToTreatmentMappingHandler<
  CorePracticeItemCode,
  ItemCodeSourceEntity
> {
  sourceEntity = new ItemCodeSourceEntity();
  translateFn = (record: ICorePracticeItemCode): IBaseMigrationItemCode => ({
    id: record.id,
    itemCode: record.itemCode,
    description: record.name,
  });

  /**
   * Core's tblChartItem/treatment rows reference tblItem by ItemId, and multiple
   * ItemIds can share one item code. Fan a single uploaded code out to every
   * sharing ItemId so each row resolves its treatment at migration time. Scoped
   * to Core, not the base, because Core's source is the only one that yields
   * several distinct ids per item code. Exact/Oasis/PracticeWorks emit exactly
   * one id per code (their source is DISTINCT/keyed by item code), so fan-out is
   * a no-op there; D4W (item_id != item code) could differ but is deliberately
   * left on the base last-write-wins.
   */
  override async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodeToTreatment()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await resolveWithConcurrencyForAll(records, DELETE_CONCURRENCY, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceRecords = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const codeToSourceInfos = new Map<string, ISourceItemInfo[]>();
    sourceRecords.forEach((record) => {
      const translated = this.translateFn(record.data);
      const itemCode = translated.itemCode.trim();
      const sourceInfo: ISourceItemInfo = {
        sourceIdentifier: this.sourceEntity
          .getSourceRecordId(record.data)
          .toString(),
        sourceLabel: translated.description
          ? `${translated.itemCode} - ${translated.description}`
          : translated.itemCode,
      };
      const existingInfos = codeToSourceInfos.get(itemCode) ?? [];
      existingInfos.push(sourceInfo);
      codeToSourceInfos.set(itemCode, existingInfos);
    });

    const treatmentConfigurations = await this.getDestinationOptions(migration);
    const treatmentConfigMap = new Map(
      treatmentConfigurations.map((config) => [normalise(config.name), config])
    );

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.flatMap((item) => {
      const code = item.code?.trim();

      if (!code) {
        return [];
      }

      const sourceInfos = codeToSourceInfos.get(code);
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;

      if (!sourceInfos?.length && item.mapTo) {
        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: 'Item code not found in source options',
        });
        return [];
      }

      if (!sourceInfos?.length || !item.mapTo) {
        return [];
      }

      const treatmentConfiguration = treatmentConfigMap.get(
        normalise(item.mapTo)
      );

      if (!treatmentConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find treatment configuration for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: `Destination identifier "${item.mapTo?.trim()}" not found`,
        });
        return [];
      }

      return sourceInfos.map((sourceInfo) => ({
        destinationIdentifier: treatmentConfiguration.ref,
        sourceIdentifier: sourceInfo.sourceIdentifier,
        sourceLabel: sourceInfo.sourceLabel,
      }));
    });

    const dedupedRecords = uniqBy(
      recordsForUpload,
      (record) => record.sourceIdentifier
    );

    const results = await this.upsertBulk(dedupedRecords, translationMap);
    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...results.failedRecords],
    });
  }

  // One row per code in the download template (Core has duplicate codes across
  // ItemIds); the upload fan-out re-expands to every ItemId.
  protected override _prepareExporterItemCodes(
    options: IBaseMigrationItemCode[]
  ): IBaseMigrationItemCode[] {
    return uniqBy(sortBy(options, 'itemCode'), (option) =>
      option.itemCode.trim()
    );
  }
}
